import React from "react";
import { Link, useParams } from "react-router-dom";
import { FileText, BookOpen, Video, Newspaper, Mic } from "lucide-react";
import MarketingHeader from "../components/argus/MarketingHeader";
import Footer from "../components/argus/Footer";

const ITEMS = [
  { kind: "Whitepaper", Icon: FileText, title: "The Anatomy of an Evidence-Backed Finding.", blurb: "Why screenshots aren't proof and what is.", tag: "Foundations" },
  { kind: "Research", Icon: BookOpen, title: "Top 10 SCA misses in npm-heavy stacks, 2026.", blurb: "We scanned 1,200 public package.json files. Here's what surprised us.", tag: "Research" },
  { kind: "Webinar", Icon: Video, title: "DAST without doing damage: domain verification 101.", blurb: "Why every public DAST scanner needs hard technical controls.", tag: "Webinar" },
  { kind: "Customer Story", Icon: Mic, title: "Customer stories — coming soon.", blurb: "Design partners onboarding now. Want to be the first?", tag: "Customer Story" },
  { kind: "Blog", Icon: Newspaper, title: "Log4Shell is four years old. Why is it still in your repo?", blurb: "On the half-life of critical CVEs in real codebases.", tag: "Blog" },
  { kind: "Guide", Icon: FileText, title: "SOC 2 evidence checklist for AppSec — 2026 edition.", blurb: "What your auditor actually asks for, by control.", tag: "Compliance" },
  { kind: "Brief", Icon: FileText, title: "DevSecOps in 12 weeks: a runbook.", blurb: "From zero to first scan-in-CI in a quarter.", tag: "Foundations" },
  { kind: "Blog", Icon: Newspaper, title: "False positives are user-experience bugs.", blurb: "Why we ship a required-reason workflow.", tag: "Blog" },
  { kind: "Research", Icon: BookOpen, title: "Six months of public AppSec disclosures, summarised.", blurb: "The patterns nobody is talking about.", tag: "Research" },
];

const FILTERS = ["All", "Foundations", "Research", "Webinar", "Customer Story", "Blog", "Compliance"];

export default function Resources() {
  const { slug } = useParams();
  const [filter, setFilter] = React.useState("All");
  const filtered = filter === "All" ? ITEMS : ITEMS.filter((i) => i.tag === filter);

  return (
    <div className="bg-paper text-ink min-h-screen flex flex-col">
      <MarketingHeader />
      <section className="mx-auto max-w-6xl w-full px-4 sm:px-6 lg:px-8 py-16">
        <p className="text-xs uppercase tracking-wider text-gold mb-3 font-semibold">Resources</p>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight" data-testid="resources-title">
          Read. Watch. Then go fix something.
        </h1>
        <p className="mt-4 text-ink/70 max-w-2xl">
          The ArgusSecure library — whitepapers, research, customer stories, and the blog. Curated for builders, not buyers.
        </p>

        <div className="mt-10 flex flex-wrap gap-2" data-testid="resource-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 h-8 rounded-md text-xs uppercase tracking-wider border transition-colors ${
                filter === f ? "bg-gold border-gold text-paper" : "border-line text-ink/60 hover:text-ink"
              }`}
              data-testid={`resource-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="resource-grid">
          {filtered.map(({ kind, Icon, title, blurb, tag }, i) => (
            <article key={i} className="rounded-xl border border-line bg-panel p-6 hover:bg-raised transition-colors" data-testid={`resource-card-${i}`}>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gold mb-3">
                <Icon size={14} /> {kind}
              </div>
              <h3 className="font-display text-xl leading-tight mb-2">{title}</h3>
              <p className="text-sm text-ink/60">{blurb}</p>
              <span className="inline-block mt-4 text-xs text-ink/40 uppercase tracking-wider">{tag}</span>
            </article>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
